import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { logger } from "@platform/browser-logger";
import {
  useCoverageNoteDrafts,
  useUpsertCoverageNote,
  useDeleteCoverageNote,
  resolveNoteKey,
} from "@platform/hooks";
import { Textarea } from "@platform/design-system";
import { extractErrorMessage } from "@platform/api";

import type { CustomToolPanelProps } from "ag-grid-react";

const MAX_LENGTH = 1024;
const AUTOSAVE_DELAY = 2000; // 2 seconds of inactivity before auto-save

export interface CoverageNotesPanelProps extends CustomToolPanelProps {
  ecoRfpId: string;
  coverageId?: string;
  benefitPlanType?: string;
}

/**
 * CoverageNotesPanel
 *
 * A sidebar panel component for AgGrid that allows editing and saving
 * coverage-specific notes.
 *
 * Behavior:
 * - Fetches existing note on mount (once via mutation)
 * - Auto-saves after X seconds of inactivity if content changed
 * - Deletes note if content is cleared
 * - Saves on unmount if there are unsaved changes
 */
export const CoverageNotesPanel = ({
  ecoRfpId,
  coverageId,
  benefitPlanType,
}: CoverageNotesPanelProps): React.ReactNode => {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sentContentRef = useRef<string>("");
  const isOwnDraftWriteRef = useRef(false);

  const {
    notes: coverageNotes,
    isLoading,
    setCoverageNote,
  } = useCoverageNoteDrafts({
    ecoRfpId: ecoRfpId || "",
    coverages: coverageId ? [{ id: coverageId, benefitPlanType }] : [],
    enabled: !!ecoRfpId && !!coverageId,
  });

  const noteKey = coverageId
    ? resolveNoteKey({ id: coverageId, benefitPlanType })
    : "";
  const notes = noteKey ? (coverageNotes[noteKey] ?? "") : "";

  const lastSavedContentRef = useRef<string>(notes);
  const currentNotesRef = useRef<string>(notes);

  // Local state drives the textarea to avoid cursor resets from async hook re-renders.
  const [textValue, setTextValue] = useState(notes);

  const upsertMutation = useUpsertCoverageNote({
    ecoRfpId: ecoRfpId || "",
    onSuccess: () => {
      logger.info("[CoverageNotesPanel] Notes saved successfully");
      lastSavedContentRef.current = sentContentRef.current;
      setHasUnsavedChanges(false);
      setSaveError(null);
    },
    onError: (error: Error) => {
      logger.error("[CoverageNotesPanel] Failed to save notes", error, {
        coverageId,
        contentLength: notes.length,
      });
      setSaveError(error.message);
    },
  });

  const deleteMutation = useDeleteCoverageNote({
    ecoRfpId: ecoRfpId || "",
    onSuccess: () => {
      logger.info("[CoverageNotesPanel] Notes deleted successfully");
      lastSavedContentRef.current = sentContentRef.current;
      setHasUnsavedChanges(false);
      setSaveError(null);
    },
    onError: (error: Error) => {
      logger.error("[CoverageNotesPanel] Failed to delete notes", error, {
        coverageId,
      });
      setSaveError(error.message);
    },
  });

  // Store mutate functions in refs to avoid stale closures
  const upsertMutateRef = useRef(upsertMutation.mutate);
  const deleteMutateRef = useRef(deleteMutation.mutate);

  // Keep refs up to date
  useEffect(() => {
    upsertMutateRef.current = upsertMutation.mutate;
    deleteMutateRef.current = deleteMutation.mutate;
  }, [upsertMutation.mutate, deleteMutation.mutate]);

  const isSaving = upsertMutation.isPending || deleteMutation.isPending;

  // Sync API-sourced notes into local state only on external changes (e.g.
  // initial load, remount, or a save completing) - not on echoes of our own
  // in-flight edits round-tripping through the shared draft cache.
  useEffect(() => {
    if (notes === currentNotesRef.current) {
      isOwnDraftWriteRef.current = false;
      return;
    }

    if (isOwnDraftWriteRef.current) {
      return;
    }

    currentNotesRef.current = notes;
    lastSavedContentRef.current = notes;
    setTextValue(notes);
    setHasUnsavedChanges(false);
    setSaveError(null);
  }, [notes]);

  // Auto-save on content change (with debounce)
  useEffect(() => {
    if (textValue === lastSavedContentRef.current || isSaving) {
      return;
    }

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      if (!coverageId) return;

      if (textValue.trim() === "") {
        sentContentRef.current = "";
        logger.info("[CoverageNotesPanel] Deleting note via auto-save", {
          coverageId,
        });
        deleteMutateRef.current({
          coverageId,
          benefitPlanType,
        });
      } else if (textValue.length <= MAX_LENGTH) {
        sentContentRef.current = textValue;
        logger.info("[CoverageNotesPanel] Upserting note via auto-save", {
          coverageId,
          contentLength: textValue.length,
        });
        upsertMutateRef.current({
          coverageId,
          content: textValue,
          benefitPlanType,
        });
      }
    }, AUTOSAVE_DELAY);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [textValue, coverageId, isSaving, benefitPlanType]);

  // Save on unmount if needed
  useEffect(
    () => () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Use ref to get current notes value (avoid stale closure)
      const currentNotes = currentNotesRef.current;

      if (currentNotes === lastSavedContentRef.current || !coverageId) {
        return;
      }

      // Only save if content is valid (not exceeding max length)
      const hasValidationError = currentNotes.length > MAX_LENGTH;
      if (hasValidationError) {
        return;
      }

      const trimmedNotes = currentNotes.trim();

      // Mirror autosave behavior: delete when notes are empty, upsert otherwise
      if (trimmedNotes === "") {
        deleteMutateRef.current({
          coverageId,
          benefitPlanType,
        });
      } else {
        upsertMutateRef.current({
          coverageId,
          content: trimmedNotes,
          benefitPlanType,
        });
      }
    },
    [coverageId, benefitPlanType],
  );

  const handleNotesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.value;
    logger.info("[CoverageNotesPanel] Notes changed", {
      oldLength: textValue.length,
      newLength: newValue.length,
      lastSavedLength: lastSavedContentRef.current.length,
    });
    if (!coverageId) {
      return;
    }

    setTextValue(newValue);
    currentNotesRef.current = newValue;
    setHasUnsavedChanges(newValue !== lastSavedContentRef.current);
    setSaveError(null);
    isOwnDraftWriteRef.current = true;
    setCoverageNote(noteKey, newValue);
  };

  if (!ecoRfpId || !coverageId) {
    return null;
  }

  const helperText = saveError
    ? extractErrorMessage(saveError, "Failed to save note")
    : isSaving
      ? "Saving..."
      : hasUnsavedChanges
        ? `${textValue.length}/${MAX_LENGTH} characters (unsaved)`
        : `${textValue.length}/${MAX_LENGTH} characters`;

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Textarea
        value={textValue}
        onChange={handleNotesChange}
        characterLimitMode="truncate"
        characterCounterVisible
        disabled={isLoading}
        error={!!saveError}
        maxLength={MAX_LENGTH}
        minRows={1}
        helperText={helperText}
        fullWidth
        sx={{
          "& .MuiInputBase-inputMultiline": {
            resize: "none",
          },
        }}
      />
    </Box>
  );
};

export default CoverageNotesPanel;
